import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { CreateModuleGracePeriod, DeleteModuleGracePeriod, GetModuleGracePeriods, UpdateModuleGracePeriod } from '../../../store/ModuleGracePeriodAction';
import TableHorizontal, { InfoObject } from "../../misc/TableHorizontal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import StatusCode from "../../../util/StatusCode";
import { CoreGracePeriod } from "../../../models/CoreModels";
import ModalComponent from "../../misc/ModalComponent";
import { Form, FormGroup, Label, Input, Button, FormFeedback } from 'reactstrap';
import ModulePeriodDateMonthPicker from "./ModulePeriodDateMonthPicker";
import { useTranslation } from "react-i18next";
import { RequestState } from "../../../models/RequestState";
import { RootState } from "../../../store/store";
import { FlowSyncAccessPolicy, isAuthorized } from '../../../models/FlowSyncAccessPolicy';

const ActionName = {
    NEW: 'NEW',
    EDIT: 'EDIT',
    DELETE: 'DELETE'
};


interface GracePeriodProps {
    moduleId: string
}

const validate = (period: Partial<CoreGracePeriod> | undefined, validateNew: boolean) => {
    const validation = [];

    if (period === undefined) {
        return [];
    }

    if (period.forceArchiveGraceDays !== undefined && period.forceArchiveGraceDays < 0) {
        validation.push("forceArchiveGraceDays");
    }

    if (period.forceDeleteGraceDays !== undefined && period.forceDeleteGraceDays < 0) {
        validation.push("forceDeleteGraceDays");
    }

    if (validateNew) {
        if (!period.forceArchiveGraceDays && !period.forceDeleteGraceDays) {
            validation.push("forceGraceDays");
        }
        if (!period.startDayOfMonth) {
            validation.push("startDayOfMonth");
        }
        if (!period.startMonth) {
            validation.push("startMonth");
        }
        if (!period.stopDayOfMonth) {
            validation.push("stopDayOfMonth");
        }
        if (!period.stopMonth) {
            validation.push("stopMonth");
        }
    }
    return validation;
}

const GracePeriods = (props: GracePeriodProps) => {
    const dispatch = useDispatch();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [name, setName] = useState<string | undefined>(undefined);
    const [period, setPeriod] = useState<Partial<CoreGracePeriod> | undefined>(undefined);
    const [saveStatus, setSaveStatus] = useState<StatusCode>(StatusCode.NONE);
    const [validateNew, setValidateNew] = useState<boolean>(false);
    const validation = validate(period, validateNew);
    const moduleGracePeriods = useSelector<RootState, RequestState<CoreGracePeriod[]>>(state => state.moduleGracePeriod.moduleGracePeriods);

    const authorizedAccessPolicies = useSelector<RootState, FlowSyncAccessPolicy[]>(state => state.me.me?.authorizedAccessPolicies ?? []);        
    const editModuleAccess = useMemo(() => {
        return isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.EDIT_MODULE);
    }, [authorizedAccessPolicies])

    const { t } = useTranslation(['configuration', 'common']);

    useEffect(() => {
        GetModuleGracePeriods(props.moduleId)(dispatch);
    }, [dispatch, props.moduleId]);

    useEffect(() => {
        if (saveStatus === StatusCode.COMPLETE) {
            setTimeout(() => {
                setSaveStatus(StatusCode.NONE);
                toggleModal();
                GetModuleGracePeriods(props.moduleId)(dispatch);
            }, 1500);
        }
    }, [saveStatus]);

    const update = async () => {
        //only validate when update
        setValidateNew(true);
        let updateValidation = validate(period, true)

        if (updateValidation.length !== 0) {
            return;
        }

        if (period === undefined) {
            return;
        }

        setSaveStatus(StatusCode.PENDING);
        if (name === ActionName.NEW) {
            const status = await CreateModuleGracePeriod(props.moduleId, period)(dispatch);
            setSaveStatus(status);
        } else if (name === ActionName.EDIT) {
            const status = await UpdateModuleGracePeriod(props.moduleId, period)(dispatch);
            setSaveStatus(status);
        } else if (name === ActionName.DELETE) {
            if (period?.id !== undefined) {
                const status = await DeleteModuleGracePeriod(props.moduleId, period.id)(dispatch);
                setSaveStatus(status);
            }
        }
    };

    const toggleModal = () => {
        setIsOpen(!isOpen);
        setName(undefined);
        setPeriod(undefined);
        setSaveStatus(StatusCode.NONE);
        setValidateNew(false);
    };

    const gracePeriods = () => {
        let gracePeriodInfo: InfoObject[] = [];
        if (moduleGracePeriods.code === StatusCode.COMPLETE) {
            gracePeriodInfo = moduleGracePeriods.data.map(x => ({
                id: x.id,
                data: [
                    { value: `${x.startDayOfMonth} ${t(`months.${x.startMonth}`, { ns: 'common' })}` },
                    { value: `${x.stopDayOfMonth} ${t(`months.${x.stopMonth}`, { ns: 'common' })}` },
                    { value: x.forceArchiveGraceDays },
                    { value: x.forceDeleteGraceDays },
                    { value: <Button className="btn-icon" disabled={!editModuleAccess} onClick={() => { setName(ActionName.EDIT); setIsOpen(true); setPeriod(x); }}><FontAwesomeIcon icon="pen" title={t('gracePeriod.editTitle')} /></Button> },
                    { value: <Button className="btn-icon" disabled={!editModuleAccess} onClick={() => { setName(ActionName.DELETE); setIsOpen(true); setPeriod(x); }}><FontAwesomeIcon icon="trash" title={t('gracePeriod.removeTitle')} /></Button> },
                ]
            })) ?? [];
        }

        return <>
            <TableHorizontal
                header={[
                    { value: t('period.startDate') },
                    { value: t('period.stopDate') },
                    { value: t('gracePeriod.forceArchiveGraceDays') },
                    { value: t('gracePeriod.forceDeleteGraceDays') },
                    { value: "", className: "w20px" },
                    { value: "", className: "w20px" }]}
                info={gracePeriodInfo}
                error={moduleGracePeriods.code === StatusCode.ERROR}
                searching={moduleGracePeriods.code === StatusCode.PENDING}
                showHeader={true}
                noResMsg={t('gracePeriod.noResult')}
            />
            <Button color="primary" disabled={!editModuleAccess} onClick={() => { setIsOpen(true); setName(ActionName.NEW); setPeriod({}); }}>{t('gracePeriod.create')}</Button>
        </>
    };

    const onChangePeriod = (name: string, value: number | undefined) => {
        let updatedPeriod = { [name]: value };
        if (name === 'startMonth') {
            updatedPeriod.startDayOfMonth = undefined;
        }
        if (name === 'stopMonth') {
            updatedPeriod.stopDayOfMonth = undefined;
        }
        setPeriod({ ...period, ...updatedPeriod })
    }

    const updateGraceDays = (name: string, value: string) => {
        let updatedValue = null;
        if (value !== '') {
            updatedValue = parseInt(value, 10);
        }
        setPeriod({ ...period, [name]: updatedValue })
    }

    const gracePeriodModal = () => {
        const actionI18nKey = name === ActionName.NEW ? "create" : name === ActionName.EDIT ? "edit" : name === ActionName.DELETE ? "remove" : "";

        const actionName = t(`action.${actionI18nKey}`, { ns: 'common' });

        return (
            <ModalComponent
                isOpen={isOpen}
                toggleModal={toggleModal}
                header={`${actionName} ${t('gracePeriod.gracePeriod')}`}
                cancel={t('action.cancel', { ns: 'common' })}
                confirm={actionName}
                saveStatus={saveStatus}
                update={() => update()}
                disabled={!editModuleAccess || validation.length > 0}
            >
                <Form>
                    <FormGroup>
                        <Label>{t('period.startDate')}</Label>
                        <ModulePeriodDateMonthPicker
                            month={period?.startMonth}
                            date={period?.startDayOfMonth}
                            monthName="startMonth"
                            dateName="startDayOfMonth"
                            disabled={name === ActionName.DELETE || !editModuleAccess}
                            onChangePeriod={(name, value) => onChangePeriod(name, value)}
                            validation={validation}
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label>{t('period.stopDate')}</Label>
                        <ModulePeriodDateMonthPicker
                            month={period?.stopMonth}
                            date={period?.stopDayOfMonth}
                            monthName="stopMonth"
                            dateName="stopDayOfMonth"
                            disabled={name === ActionName.DELETE || !editModuleAccess}
                            onChangePeriod={(name, value) => onChangePeriod(name, value)}
                            validation={validation}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label for="forceArchiveGraceDays">{t('gracePeriod.forceArchiveGraceDays')}</Label>
                        <Input
                            type="number"
                            name="forceArchiveGraceDays"
                            id="forceArchiveGraceDays"
                            placeholder={t('gracePeriod.forceArchiveGraceDays')}
                            autoComplete="off"
                            value={period?.forceArchiveGraceDays || ''}
                            disabled={name === ActionName.DELETE || !editModuleAccess}
                            onChange={(e) => updateGraceDays("forceArchiveGraceDays", e.target.value)}
                            invalid={validation.indexOf("forceGraceDays") !== -1 || validation.indexOf("forceArchiveGraceDays") !== -1}
                        />
                        <FormFeedback>{validation.indexOf("forceGraceDays") !== -1 ? t('gracePeriod.forceGraceDaysRequired')
                            : validation.indexOf("forceArchiveGraceDays") !== -1 ? t('gracePeriod.forceGraceDaysInvalid') : ''}
                        </FormFeedback>
                    </FormGroup>

                    <FormGroup>
                        <Label for="forceDeleteGraceDays">{t('gracePeriod.forceDeleteGraceDays')}</Label>
                        <Input
                            type="number"
                            name="forceDeleteGraceDays"
                            id="forceDeleteGraceDays"
                            placeholder={t('gracePeriod.forceDeleteGraceDays')}
                            autoComplete="off"
                            value={period?.forceDeleteGraceDays || ""}
                            disabled={name === ActionName.DELETE || !editModuleAccess}
                            onChange={(e) => updateGraceDays("forceDeleteGraceDays", e.target.value)}
                            invalid={validation.indexOf("forceGraceDays") !== -1 || validation.indexOf("forceDeleteGraceDays") !== -1}
                        />
                        <FormFeedback>{validation.indexOf("forceGraceDays") !== -1 ? t('gracePeriod.forceGraceDaysRequired')
                            : validation.indexOf("forceDeleteGraceDays") !== -1 ? t('gracePeriod.forceGraceDaysInvalid') : ''}
                        </FormFeedback>
                    </FormGroup>
                </Form>
            </ModalComponent>
        );
    }

    return (
        <div className="mt-4">
            <h5>{t('gracePeriod.heading')}</h5>
            <p>{t('gracePeriod.ingress')}</p>

            {gracePeriods()}
            {isOpen && period && gracePeriodModal()}
        </div>
    );
}

export default GracePeriods;
